import type { Attribut } from '@/models/interfaces/Attribut'
import type { CompetenceAttribut } from '@/models/interfaces/CompetenceAttribut'
import { apiClient } from '@/plugins/axios/api'

interface HydraView {
  'hydra:next'?: string
}

interface HydraCollection<T> {
  'hydra:member'?: T[]
  'hydra:view'?: HydraView
}

const ATTRIBUT_KIND_INDICATEUR = 'I'

/** Normalize API row (camelCase or snake_case) to Attribut so atgNature and other fields are always present. */
function normalizeAttribut(row: Record<string, unknown>): Attribut {
  const atgId = (row.atgId ?? row.atg_id) as number
  const iri = (row['@id'] ?? row.iri ?? `/api/attributs/${atgId}`) as string
  return {
    '@id': iri,
    atgId,
    atgRefCode: String(row.atgRefCode ?? row.atg_ref_code ?? ''),
    atgTitre: String(row.atgTitre ?? row.atg_titre ?? ''),
    // Gardé tel quel, absent compris, pour que libelleDynamique retombe sur le titre français.
    atgTitreEn: (row.atgTitreEn ?? row.atg_titre_en) as string | null | undefined,
    atgNature: String(row.atgNature ?? row.atg_nature ?? ''),
    atgStatut: String(row.atgStatut ?? row.atg_statut ?? ''),
    atgKind: String(row.atgKind ?? row.atg_kind ?? ''),
    atgCode: String(row.atgCode ?? row.atg_code ?? ''),
    atgIcon: (row.atgIcon ?? row.atg_icon) as string | undefined,
    atgDescription: (row.atgDescription ?? row.atg_description) as string | undefined,
    atgObligatoire: (row.atgObligatoire ?? row.atg_obligatoire) as boolean | undefined,
    createdAt: (row.createdAt ?? row.created_at) as string | undefined,
    updatedAt: (row.updatedAt ?? row.updated_at) as string | undefined,
    atgGeographicScope: (row.atgGeographicScope ?? row.atg_geographic_scope) as string | undefined,
    atgStyleJson: (row.atgStyleJson ?? row.atg_style_json) as Record<string, unknown> | null | undefined,
    fkIndicateurPresetId: (row.fkIndicateurPresetId ?? row.fk_indicateur_preset_id) as number | null | undefined,
    atgGeomTarget: (row.atgGeomTarget ?? row.atg_geom_target) as string | undefined,
    atgGeomCatRefCode: (row.atgGeomCatRefCode ?? row.atg_geom_cat_ref_code) as string | undefined,
    fkSousdomaineId: (row.fkSousdomaineId ?? row.fk_sousdomaine_id ?? row.fkSousDomaineId ?? row.fk_sous_domaine_id) as
      | number
      | null
      | undefined,
  }
}

/** Follows hydra:view.hydra:next across pages, normalizing every row. Shared by getAll and getIndicateursPcd. */
async function fetchAllAttributs(firstPageUrl: string): Promise<Attribut[]> {
  const all: Attribut[] = []
  let url: string | null = firstPageUrl
  while (url) {
    const res = await apiClient.get(url)
    const data = res.data as HydraCollection<Record<string, unknown>>
    const member = data['hydra:member'] ?? []
    const items = Array.isArray(member) ? member : []
    all.push(...items.map((row) => normalizeAttribut(row as Record<string, unknown>)))
    const view: HydraView | undefined = data['hydra:view']
    url = view?.['hydra:next'] ?? null
  }
  return all
}

export class AttributService {
  static async getAll(): Promise<Attribut[]> {
    try {
      return await fetchAllAttributs('/api/attributs')
    } catch {
      console.debug('Attribut endpoint not available yet, returning empty array.')
      return []
    }
  }

  /**
   * The 53 indicateurs of the PCD classification file (direct attribut.fkSousdomaineId link).
   * Filters server-side (atgKind, atgGeographicScope — GET /api/attributs ApiFilter) instead of
   * loading all attributs, then keeps only fkSousdomaineId !== null as a client-side safety net.
   */
  static async getIndicateursPcd(): Promise<Attribut[]> {
    try {
      const all = await fetchAllAttributs(
        '/api/attributs?atgKind=I&atgGeographicScope=cmn&itemsPerPage=100',
      )
      return all.filter((a) => a.fkSousdomaineId != null)
    } catch {
      console.debug('Attribut endpoint not available yet, returning empty array.')
      return []
    }
  }

  /**
   * GET /api/attributs/{id}
   * Returns a single attribut by id.
   */
  static async getById(id: number): Promise<Attribut | null> {
    try {
      const response = await apiClient.get<Attribut>(`/api/attributs/${encodeURIComponent(String(id))}`)
      return response.data ?? null
    } catch {
      console.debug('Attribut getById failed.')
      return null
    }
  }

  /**
   * Returns attributs with atgKind === 'I' (indicators). When API exposes fk_indicateur_preset_id, only includes those with it set.
   */
  static async getIndicateurs(): Promise<Attribut[]> {
    try {
      const all = await this.getAll()
      return all.filter((a) => {
        if (a.atgKind !== ATTRIBUT_KIND_INDICATEUR) return false
        const presetId = a.fkIndicateurPresetId ?? (a as unknown as Record<string, unknown>).fk_indicateur_preset_id
        if (presetId !== undefined) return presetId != null
        return true
      })
    } catch {
      return []
    }
  }

  static async getCompetenceAttributs(): Promise<CompetenceAttribut[]> {
    try {
      const all: CompetenceAttribut[] = []
      let url: string | null = '/api/competence_attributs'
      while (url) {
        const response: { data: HydraCollection<CompetenceAttribut> } = await apiClient.get(url)
        const member = response.data['hydra:member'] ?? []
        all.push(...member)
        const view: HydraView | undefined = response.data['hydra:view']
        url = view?.['hydra:next'] ?? null
      }
      return all
    } catch {
      console.debug('CompetenceAttribut endpoint not available yet, returning empty array.')
      return []
    }
  }

  static async getIndicateursByCompetenceId(cptId: number): Promise<Attribut[]> {
    try {
      const [attributs, competenceAttributs] = await Promise.all([
        this.getAll(),
        this.getCompetenceAttributs(),
      ])

      const attributIds = competenceAttributs
        .filter((ca) => ca.fkCptId === cptId)
        .map((ca) => ca.fkAttributId)

      return attributs.filter(
        (attribut) => attributIds.includes(attribut.atgId) && attribut.atgKind === 'I',
      )
    } catch {
      console.debug('Error fetching indicateurs by competence id, returning empty array.')
      return []
    }
  }

  /**
   * POST /api/attributs
   * Create an attribut (e.g. for infrastructure, atgKind = 'A', atgStatut = 'ACTIF').
   */
  static async create(body: {
    atgRefCode: string
    atgTitre: string
    /** Titre anglais, facultatif : l'attribut peut n'être saisi qu'en français. */
    atgTitreEn?: string
    atgNature: string
    atgDescription?: string
    atgKind?: string
    atgStatut?: string
  }): Promise<Attribut> {
    const response = await apiClient.post<Attribut>('/api/attributs', {
      atgKind: 'A',
      atgStatut: 'ACTIF',
      ...body,
    }, { headers: { 'Content-Type': 'application/json' } })
    return response.data
  }

  /**
   * PATCH /api/attributs/{id}
   * Partial update (merge-patch). Only sent fields are updated; required fields (atgRefCode, atgTitre, atgNature) are not required.
   * Sends both camelCase and snake_case so the backend accepts the update regardless of its serialization convention.
   */
  static async update(
    id: number,
    body: Partial<
      Pick<
        Attribut,
        | 'atgTitre'
        | 'atgTitreEn'
        | 'atgDescription'
        | 'atgNature'
        | 'atgStatut'
        | 'fkIndicateurPresetId'
      >
    >,
  ): Promise<Attribut> {
    const payload: Record<string, unknown> = { ...body }
    if (payload.fkIndicateurPresetId !== undefined && payload.fkIndicateurPresetId !== null && typeof payload.fkIndicateurPresetId !== 'number') {
      const n = Number(payload.fkIndicateurPresetId)
      payload.fkIndicateurPresetId = Number.isFinite(n) ? n : null
    }
    if (payload.atgStatut !== undefined) payload.atg_statut = payload.atgStatut
    if (payload.atgTitre !== undefined) payload.atg_titre = payload.atgTitre
    if (payload.atgTitreEn !== undefined) payload.atg_titre_en = payload.atgTitreEn
    if (payload.atgDescription !== undefined) payload.atg_description = payload.atgDescription
    if (payload.atgNature !== undefined) payload.atg_nature = payload.atgNature
    if (payload.fkIndicateurPresetId !== undefined) payload.fk_indicateur_preset_id = payload.fkIndicateurPresetId
    const response = await apiClient.patch<Record<string, unknown>>(
      `/api/attributs/${encodeURIComponent(String(id))}`,
      payload,
      { headers: { 'Content-Type': 'application/merge-patch+json' } },
    )
    return normalizeAttribut(response.data ?? {})
  }

  /**
   * DELETE /api/attributs/{id}
   * Permanently delete an attribut. Requires backend support.
   */
  static async delete(id: number): Promise<void> {
    await apiClient.delete(`/api/attributs/${encodeURIComponent(String(id))}`)
  }

  /**
   * GET /api/attributs/{id}/type_ouvrages
   * Returns the list of type d'ouvrage (infrastructures) linked to this attribut.
   * Backend: table referentiel.attribut_type_ouvrage (fk_atg_id, fk_typ_id). One row = one link.
   * API response shape: [{ typId, typeOuvrageCode, typeOuvrageLib }, ...]
   */
  static async getTypeOuvragesForAttribut(atgId: number): Promise<Array<{ typId: number; code: string; lib: string }>> {
    try {
      const response = await apiClient.get<{ 'hydra:member'?: unknown[] } | unknown[]>(
        `/api/attributs/${encodeURIComponent(String(atgId))}/type_ouvrages`,
      )
      const raw = Array.isArray(response.data)
        ? response.data
        : (response.data as { 'hydra:member'?: unknown[] })?.['hydra:member'] ?? []
      return raw.map((item) => {
        if (typeof item === 'string') {
          const match = item.match(/\/type_ouvrages?\/(\d+)/)
          const typId = match ? parseInt(match[1], 10) : 0
          return { typId, code: String(typId), lib: String(typId) }
        }
        const o = item as Record<string, unknown>
        let typId = Number(o.id ?? o.typId ?? (o as Record<string, unknown>).typ_id ?? 0)
        if (!typId && (o['@id'] ?? o.iri)) {
          const iri = String(o['@id'] ?? o.iri)
          const m = iri.match(/\/(\d+)$/)
          typId = m ? parseInt(m[1], 10) : 0
        }
        const code = String(
          o.typeOuvrageCode ?? o.type_ouvrage_code ?? (o as Record<string, unknown>).code ?? '',
        ).trim()
        const lib = String(
          o.typeOuvrageLib ?? o.type_ouvrage_lib ?? (o as Record<string, unknown>).libelle ?? code,
        ).trim()
        return { typId, code: code || String(typId), lib: lib || code || String(typId) }
      }).filter((x) => x.typId > 0)
    } catch {
      return []
    }
  }

  /**
   * PUT /api/attributs/{id}/type_ouvrages
   * Replaces the full list of links for this attribut (referentiel.attribut_type_ouvrage).
   * Body: { typIds: [1, 2, 3, ...] } = fk_typ_id list. To add: current list + new typ_id; to remove: current list without that typ_id; to unlink all: [].
   */
  static async putTypeOuvragesForAttribut(atgId: number, typIds: number[]): Promise<void> {
    await apiClient.put(
      `/api/attributs/${encodeURIComponent(String(atgId))}/type_ouvrages`,
      { typIds },
      { headers: { 'Content-Type': 'application/json' } },
    )
  }

  /**
   * POST /api/competence_attributs
   * Link an attribut to a competence.
   */
  static async linkCompetenceAttribut(fkCptId: number, fkAttributId: number): Promise<CompetenceAttribut> {
    const response = await apiClient.post<CompetenceAttribut>('/api/competence_attributs', {
      fkCptId,
      fkAttributId,
    }, { headers: { 'Content-Type': 'application/json' } })
    return response.data
  }

  // TODO: Fix 404 error - API endpoint /api/attributs/{cptId} is not the right endpoint
  // static async getAttributsByCompetenceId(cptId: number): Promise<Attribut[]> {
  //   try {
  //     const response = await apiClient.get(`/api/attributs/${cptId}`)
  //     const attributs = response.data['hydra:member'] || response.data || []
  //     // Filter on atgKind: "A" (A for attribut)
  //     return attributs.filter((attribut: Attribut) => attribut.atgKind === 'A')
  //   } catch (error) {
  //     console.debug('Error fetching attributs by competence id, returning empty array.', error)
  //     return []
  //   }
  // }
}
